// Messages: every text and email the company sent.
import { Link } from "@tanstack/react-router";
import { type OutboxItem, useOutbox, useSettings, useUpdateSettings } from "../../api/hooks";
import { useSession } from "../../app/session";
import { DataTable } from "../../components/DataTable";
import { useToast } from "../../components/overlays";
import { ago, Card, SettingRow, type Status, StatusLabel, Switch } from "../../components/parts";
import { errorText } from "./common";

const TEMPLATE_WORDS: Record<string, string> = {
  sign_in_code: "Sign-in code",
  invite: "Team invite",
  reminder: "Visit reminder",
  reply: "Reply",
  manual: "Message",
  invoice: "Invoice",
  campaign: "Campaign",
  "auto.review_request": "Review request",
  "alert.rating": "Low rating alert",
};

const STATUS_WORDS: Record<string, [Status, string]> = {
  dry_run: ["informative", "Test mode, not sent"],
  held: ["warning", "Waiting for 8 AM"],
  queued: ["warning", "Waiting to send"],
  sent: ["success", "Sent"],
  failed: ["critical", "Failed"],
  cancelled: ["inactive", "Stopped, they opted out"],
};

export function MessagesSettings() {
  const { company, role } = useSession();
  const s = useSettings(company.id);
  const box = useOutbox(company.id);
  const save = useUpdateSettings();
  const toast = useToast();
  const live = s.data?.settings.messaging.live ?? false;
  return (
    <>
      <Card id="sending" title="Sending" flush>
        <SettingRow
          title="Real texts, emails and card charges"
          description="Off means messages are saved below but nothing goes to clients or crew, and only Stripe test keys work. Only the owner can turn it on, once the server is set to go live."
          control={
            <Switch
              label="Real texts, emails and card charges"
              checked={live}
              disabled={role !== "owner"}
              onChange={(v) =>
                save.mutate(
                  { messaging: { live: v } },
                  {
                    onSuccess: () => toast(v ? "Real texts, emails and card charges are on" : "Real sending is off"),
                    onError: (e) => toast(errorText(e)),
                  },
                )
              }
            />
          }
        />
        <SettingRow
          title="Automatic messages"
          description="Reminders, follow-ups and receipts each have their own switch."
          control={
            <Link to="/settings/automations" className="btn tertiary small">
              Open
            </Link>
          }
        />
        <SettingRow
          title="Quiet hours"
          description="Texts wait from 8 PM to 8 AM, marketing texts from 7 PM to 9 AM. Emails go any time."
          control={<span className="muted">Always on</span>}
        />
      </Card>
      <DataTable<OutboxItem>
        title="Message log"
        rows={box.data?.items ?? []}
        rowKey={(m) => m.id}
        search={(m) => `${m.to} ${TEMPLATE_WORDS[m.template] ?? m.template}`}
        empty="No texts or emails yet. Invites and sign-in codes show here."
        columns={[
          {
            key: "when",
            header: "When",
            sort: (m) => -Date.parse(m.createdAt),
            render: (m) => <span className="num">{ago(m.createdAt)}</span>,
          },
          { key: "what", header: "What", render: (m) => <b>{TEMPLATE_WORDS[m.template] ?? m.template}</b> },
          { key: "to", header: "To", render: (m) => <span className="num">{m.to}</span> },
          { key: "how", header: "By", render: (m) => (m.channel === "sms" ? "Text" : m.channel === "email" ? "Email" : "Push") },
          {
            key: "status",
            header: "Status",
            render: (m) => {
              const [st, word] = STATUS_WORDS[m.status] ?? ["inactive", m.status];
              return <StatusLabel status={st}>{word}</StatusLabel>;
            },
          },
        ]}
      />
    </>
  );
}
